import React, { useState, useEffect } from 'react';
import {
  FolderKanban,
  Calendar,
  Users,
  Clock,
  CheckCircle,
  Circle,
  AlertCircle,
  Plus,
  Filter,
  Search,
  ChevronRight,
  MoreHorizontal,
  Flag,
  Target,
  Layers,
  BarChart3,
  ListTodo
} from 'lucide-react';

// Componente de tarjeta de proyecto
const ProjectCard = ({ project, onClick }) => {
  const statusColors = {
    activo: 'bg-green-50 text-green-700',
    pausado: 'bg-yellow-50 text-yellow-700',
    completado: 'bg-blue-50 text-blue-700',
    retrasado: 'bg-red-50 text-red-700'
  };

  const progressColor = project.progreso >= 75 ? 'bg-green-500' :
                        project.progreso >= 50 ? 'bg-blue-500' :
                        project.progreso >= 25 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-100 p-5 cursor-pointer hover:border-[#3B82F6] transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#3B82F6] bg-opacity-10 rounded-lg">
            <FolderKanban className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <h4 className="font-medium text-[#111827]">{project.nombre}</h4>
            <p className="text-sm text-[#6B7280]">{project.cliente}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColors[project.estado]}`}>
          {project.estado}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[#6B7280]">Progreso</span>
          <span className="font-medium text-[#111827]">{project.progreso}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${progressColor} rounded-full transition-all duration-500`}
            style={{ width: `${project.progreso}%` }}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-[#6B7280]">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <ListTodo className="w-3 h-3" />
            {project.tareasCompletadas}/{project.tareasTotales}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {project.equipo}
          </span>
        </div>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {project.fechaFin}
        </span>
      </div>
    </div>
  );
};

// Componente de tarea
const TaskItem = ({ task }) => {
  const priorityColors = {
    alta: 'text-red-500',
    media: 'text-yellow-500',
    baja: 'text-green-500'
  };

  const statusIcons = {
    pendiente: Circle,
    en_progreso: Clock,
    completada: CheckCircle
  };

  const StatusIcon = statusIcons[task.estado] || Circle;

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
      <StatusIcon className={`w-5 h-5 ${task.estado === 'completada' ? 'text-green-500' : 'text-[#9CA3AF]'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${task.estado === 'completada' ? 'text-[#9CA3AF] line-through' : 'text-[#111827]'}`}>
          {task.nombre}
        </p>
        <p className="text-xs text-[#6B7280]">{task.proyecto}</p>
      </div>
      <Flag className={`w-4 h-4 ${priorityColors[task.prioridad]}`} />
      <div className="flex -space-x-2">
        {task.asignados?.map((persona, i) => (
          <div key={i} className="w-6 h-6 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-xs border-2 border-white">
            {persona.charAt(0)}
          </div>
        ))}
      </div>
      <span className="text-xs text-[#6B7280]">{task.vencimiento}</span>
    </div>
  );
};

// Componente de barra de timeline simplificada
const TimelineBar = ({ project }) => {
  const diasRestantes = parseInt(project.diasRestantes) || 0;
  const totalDias = parseInt(project.totalDias) || 30;
  const progreso = ((totalDias - diasRestantes) / totalDias) * 100;

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl">
      <div className="w-40 truncate">
        <p className="text-sm font-medium text-[#111827]">{project.nombre}</p>
        <p className="text-xs text-[#6B7280]">{project.cliente}</p>
      </div>
      <div className="flex-1">
        <div className="h-6 bg-gray-100 rounded-lg overflow-hidden relative">
          <div
            className="h-full bg-[#3B82F6] rounded-lg"
            style={{ width: `${progreso}%` }}
          />
          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
            {project.diasRestantes} dias restantes
          </span>
        </div>
      </div>
      <span className="text-sm text-[#6B7280] w-20 text-right">{project.fechaFin}</span>
    </div>
  );
};

export default function ProyectosPanel() {
  const [activeTab, setActiveTab] = useState('proyectos');
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [kpis] = useState({
    activos: { value: 8, nuevos: 2 },
    tareasPendientes: { value: 34, urgentes: 5 },
    cumplimiento: { value: '87%', tendencia: '+5%' },
    horasEstimadas: { value: '156h', porcentaje: '68%' }
  });

  const [projects] = useState([
    { nombre: 'Instalacion TPV Hotel Mar', cliente: 'Hotel Mar Azul', estado: 'activo', progreso: 75, tareasCompletadas: 12, tareasTotales: 16, equipo: 3, fechaFin: '25 Ene', diasRestantes: '9', totalDias: '30' },
    { nombre: 'Migracion Software Rest.', cliente: 'Restaurante El Puerto', estado: 'activo', progreso: 45, tareasCompletadas: 9, tareasTotales: 20, equipo: 2, fechaFin: '15 Feb', diasRestantes: '30', totalDias: '45' },
    { nombre: 'Red Franquicia Norte', cliente: 'Franquicia Barcelona', estado: 'retrasado', progreso: 30, tareasCompletadas: 6, tareasTotales: 20, equipo: 4, fechaFin: '20 Ene', diasRestantes: '4', totalDias: '60' },
    { nombre: 'Integracion Contabilidad', cliente: 'Cafe Central', estado: 'pausado', progreso: 60, tareasCompletadas: 6, tareasTotales: 10, equipo: 1, fechaFin: '10 Feb', diasRestantes: '25', totalDias: '30' },
    { nombre: 'Sistema Reservas Online', cliente: 'Hotel Costa Brava', estado: 'activo', progreso: 90, tareasCompletadas: 18, tareasTotales: 20, equipo: 2, fechaFin: '18 Ene', diasRestantes: '2', totalDias: '45' },
    { nombre: 'Actualizacion Hardware', cliente: 'Gym Fitness Plus', estado: 'completado', progreso: 100, tareasCompletadas: 8, tareasTotales: 8, equipo: 2, fechaFin: '10 Ene', diasRestantes: '0', totalDias: '15' }
  ]);

  const [tasks] = useState([
    { nombre: 'Configurar servidor de backups', proyecto: 'Hotel Mar Azul', estado: 'en_progreso', prioridad: 'alta', asignados: ['Carlos', 'Ana'], vencimiento: 'Hoy' },
    { nombre: 'Instalar terminales TPV', proyecto: 'Hotel Mar Azul', estado: 'pendiente', prioridad: 'alta', asignados: ['Miguel'], vencimiento: 'Manana' },
    { nombre: 'Migrar base de datos', proyecto: 'Rest. El Puerto', estado: 'pendiente', prioridad: 'media', asignados: ['Carlos'], vencimiento: '20 Ene' },
    { nombre: 'Pruebas de integracion', proyecto: 'Hotel Costa Brava', estado: 'en_progreso', prioridad: 'alta', asignados: ['Ana', 'Laura'], vencimiento: 'Hoy' },
    { nombre: 'Documentacion usuario', proyecto: 'Hotel Costa Brava', estado: 'pendiente', prioridad: 'baja', asignados: ['Laura'], vencimiento: '18 Ene' },
    { nombre: 'Revision cableado red', proyecto: 'Franquicia Barcelona', estado: 'completada', prioridad: 'media', asignados: ['Miguel'], vencimiento: '15 Ene' }
  ]);

  const tabs = [
    { id: 'proyectos', label: 'Proyectos', icon: FolderKanban },
    { id: 'tareas', label: 'Tareas', icon: ListTodo },
    { id: 'timeline', label: 'Timeline', icon: BarChart3 }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Proyectos</h1>
          <p className="text-[#6B7280] mt-1">Gestion de proyectos y tareas</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Nuevo proyecto</span>
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Proyectos activos</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.activos.value}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <FolderKanban className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">+{kpis.activos.nuevos} este mes</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Tareas pendientes</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.tareasPendientes.value}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-xl">
              <ListTodo className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-xs text-red-600 mt-2">{kpis.tareasPendientes.urgentes} urgentes</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Cumplimiento</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.cumplimiento.value}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <Target className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.cumplimiento.tendencia} vs mes anterior</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Horas estimadas</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.horasEstimadas.value}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-[#6B7280] mt-2">{kpis.horasEstimadas.porcentaje} consumido</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-[#111827] shadow-sm'
                  : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6] w-64"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'proyectos' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} />
          ))}
        </div>
      )}

      {activeTab === 'tareas' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-[#111827]">Todas las tareas</h2>
            <div className="flex items-center gap-2 text-sm text-[#6B7280]">
              <span>{tasks.filter(t => t.estado === 'completada').length} completadas</span>
              <span>de {tasks.length}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {tasks.map((task, index) => (
              <TaskItem key={index} task={task} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-[#111827]">Timeline de proyectos</h2>
          </div>
          <div className="p-4 space-y-2">
            {projects.filter(p => p.estado !== 'completado').map((project, index) => (
              <TimelineBar key={index} project={project} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
